import {AlertTriangle, Bot, ChevronDown, ChevronRight, Send, User, X} from "lucide-react";
import React, {useEffect, useRef, useState} from "react";
import {ApiBankInfo, ApiBuildingInfo, ApiDeviceInfo} from "./solutrakApiService";
import {BankAndDeviceSelectors} from "../../components/NewAlternativeView/BankAndDeviceSelectors";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import {ChatMessage} from "../../../types/chat";
import {useCookies} from "react-cookie";
import './NewAlternateView.css';

type ElevatorIQAssistantViewProps = {
    setShowAssistant:  React.Dispatch<React.SetStateAction<boolean>>;
    selectedBuilding: ApiBuildingInfo | null;
    selectedBank: ApiBankInfo | null;
    selectedDevice: ApiDeviceInfo | null;
}

export default function ElevatorIQAssistantView({setShowAssistant, selectedBuilding, selectedBank: currentSelectedBank, selectedDevice: currentSelectedDevice}: ElevatorIQAssistantViewProps) {
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
        {
            role: 'assistant',
            content: 'Hello! I\'m your ElevatorIQ Assistant. How can I help you with elevator operations today?',
            timestamp: new Date()
        }
    ]);
    const [currentMessage, setCurrentMessage] = useState('');

    const [selectedBank, setSelectedBank] = useState<ApiBankInfo | null>(currentSelectedBank);
    const [selectedDevice, setSelectedDevice] = useState<ApiDeviceInfo | null>(currentSelectedDevice);
    const [isSelectorCollapsed, setIsSelectorCollapsed] = useState(!!currentSelectedDevice);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cookies] = useCookies();
    const token = cookies.token;
    const chatContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Scroll to the bottom of the chat container when messages change
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages, isLoading]);

    console.log("ElevatorIQ Assistant View initialized with token:", token);

    const handleBankSelect = (bank: ApiBankInfo) => {
        setSelectedBank(bank);
        // If the current selected device is not in the newly selected bank, reset it.
        // Or, auto-select the first device of the new bank.
        if (!bank.deviceInfos.find(device => device.deviceId === selectedDevice?.deviceId)) {
            setSelectedDevice(null);
            // Optionally, select the first device of the new bank:
            // if (bank.deviceInfos && bank.deviceInfos.length > 0) {
            //   setSelectedDevice(bank.deviceInfos[0]);
            // }
        }
    };

    const handleDeviceSelect = (device: ApiDeviceInfo) => {
        setSelectedDevice(device);
        setIsSelectorCollapsed(true);
    };

    const handleSendMessage = async () => {
        if (!currentMessage.trim() || !selectedDevice || isLoading) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: currentMessage,
            timestamp: new Date()
        };

        // This message will be updated as the stream comes in
        const assistantMessagePlaceholder: ChatMessage = {
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            thoughts: [], // Use a simple array for thoughts
            isThinking: true,
        };

        setChatMessages([...chatMessages, userMessage, assistantMessagePlaceholder]);
        setCurrentMessage('');
        setIsLoading(true);
        setError(null);

        const history = chatMessages
            .filter(msg => msg.role === 'user' || (msg.role === 'assistant' && !msg.isThinking)) // Exclude placeholders
            .map(msg => ({
                role: msg.role === 'user' ? 'human' : (msg.role === 'assistant' ? 'ai' : msg.role),
                content: msg.content
            }));

        const endpoint = 'http://localhost:8000/api/diagnostics/ask_stream';
        const useDummyData = false; // TOGGLE FOR REAL/DUMMY DATA
        const finalEndpoint = useDummyData ? 'http://localhost:8000/api/diagnostics/ask_stream_dummy' : endpoint;

        fetchEventSource(finalEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Valid-token': token,
            },
            body: JSON.stringify({
                query: currentMessage,
                deviceId: selectedDevice.deviceId,
                chat_history: history,
            }),
            onmessage(ev) {
                const eventData = JSON.parse(ev.data);
                setChatMessages(prevMessages => {
                    const newMessages = [...prevMessages];
                    const lastMessageIndex = newMessages.length - 1;
                    const lastMessage = newMessages[lastMessageIndex];

                    if (lastMessage && lastMessage.role === 'assistant') {
                        // Create a new message object to ensure immutability
                        const updatedMessage = { ...lastMessage };
                        console.log("Received event:", eventData.type);

                        switch (eventData.type) {
                            case 'thought':
                                // Ensure thoughts array exists and add the new thought
                                updatedMessage.thoughts = [...(updatedMessage.thoughts || []), eventData.content];
                                break;
                            case 'final_answer':
                                updatedMessage.content = eventData.content;
                                updatedMessage.isThinking = false;
                                if (updatedMessage.thoughts && updatedMessage.thoughts.length > 0) {
                                    updatedMessage.areThoughtsCollapsed = true;
                                }
                                setIsLoading(false);
                                break;
                            case 'error':
                                updatedMessage.content = `An error occurred: ${eventData.content}`;
                                updatedMessage.isThinking = false;
                                setError(eventData.content);
                                setIsLoading(false);
                                break;
                        }
                        // Replace the old message with the updated one
                        newMessages[lastMessageIndex] = updatedMessage;
                    }
                    return newMessages;
                });
            },
            onerror(err) {
                console.error("EventSource failed:", err);
                const errorMessage = "Sorry, I couldn't connect to the server. Please try again later.";
                setError(errorMessage);
                setChatMessages(prevMessages => {
                    const newMessages = [...prevMessages];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage && lastMessage.role === 'assistant') {
                        lastMessage.content = errorMessage;
                        lastMessage.isThinking = false;
                    }
                    return newMessages;
                });
                setIsLoading(false);
                // The library will automatically retry, but we want to stop it
                throw err;
            }
        });
    };

    const toggleThoughts = (messageIndex: number) => {
        setChatMessages(currentMessages =>
            currentMessages.map((msg, idx) => {
                if (idx === messageIndex) {
                    return { ...msg, areThoughtsCollapsed: !msg.areThoughtsCollapsed };
                }
                return msg;
            })
        );
    };

    return (
        <div
            className="fixed right-0 top-0 h-full w-[500px] bg-slate-800/95 backdrop-blur-sm shadow-2xl shadow-black/20 border-l border-slate-700/50 transform transition-transform duration-300 flex flex-col z-[200]">
            <div className="p-6 border-b border-slate-700/50">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">ElevatorIQ Assistant</h3>
                    <button
                        onClick={() => setShowAssistant(false)}
                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-slate-400"/>
                    </button>
                </div>
            </div>
            {(() => {
                if (!isSelectorCollapsed) {
                    /* Bank and Device Selectors for Maintenance */
                    return (
                        <BankAndDeviceSelectors
                            selectedBuilding={selectedBuilding}
                            selectedBank={selectedBank}
                            selectedDevice={selectedDevice}
                            handleBankSelect={handleBankSelect}
                            handleDeviceSelect={handleDeviceSelect} />
                    );
                } else {
                    return (
                        <div className="p-4 flex items-center justify-between bg-slate-700/50 border-b border-slate-700/50">
                            <div className="text-sm text-slate-300">
                                {selectedBank ? `Bank: ${selectedBank.bankName}` : 'No bank selected'}
                                {selectedDevice ? ` | Device: ${selectedDevice.deviceName}` : ' | No device selected'}
                            </div>
                            <button
                                onClick={() => {
                                    setIsSelectorCollapsed(false);
                                    setSelectedDevice(null);
                                    setSelectedBank(null);
                                }}
                                className="text-sm text-blue-400 hover:underline"
                            >
                                Change
                            </button>
                        </div>
                    );
                }
            })()}

            {selectedDevice ? (
                <>
                    <div className="flex-1 overflow-y-auto" ref={chatContainerRef}>
                        {/* Chat Section */}
                        <div className="p-6 space-y-6">
                            {chatMessages.map((message, index) => (
                                <div key={index} className="flex items-start space-x-4">
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${message.role === 'user' ? 'bg-blue-500' : 'bg-slate-600'}`}>
                                        {message.role === 'user' ? <User size={18} className="text-white" /> : <Bot size={18} className="text-white" />}
                                    </div>

                                    <div className="flex-1">
                                        <div
                                            className={`max-w-full rounded-lg px-4 py-3 ${
                                                message.role === 'user'
                                                    ? 'bg-blue-500 text-white'
                                                    : message.isThinking
                                                        ? 'thinking-gradient-border text-slate-200'
                                                        : 'bg-slate-700 text-slate-200'
                                            }`}
                                        >
                                            {message.isThinking && !message.content && (
                                                <div className="flex items-center space-x-2">
                                                    Thinking...
                                                </div>
                                            )}
                                            {message.content && <p className="whitespace-pre-wrap">{message.content}</p>}
                                            {message.thoughts && message.thoughts.length > 0 && !message.isThinking && (
                                                <button onClick={() => toggleThoughts(index)} className="flex items-center text-sm text-slate-400 mt-2">
                                                    {message.areThoughtsCollapsed ? <ChevronRight size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
                                                    <span>{message.areThoughtsCollapsed ? 'Show' : 'Hide'} thoughts</span>
                                                </button>
                                            )}

                                            {message.thoughts && message.thoughts.length > 0 && message.isThinking && (
                                                <div className="space-y-2 pb-2">
                                                    <div className="flex items-center text-sm text-slate-400 mb-2">
                                                        <ChevronDown size={16} className="mr-1" />
                                                        <span>Thinking...</span>
                                                    </div>
                                                    {message.thoughts.map((thought, i) => (
                                                        <div key={i} className="flex items-center text-sm text-slate-400 pl-5">
                                                            <div className="w-2 h-2 bg-slate-500 rounded-full mr-3 animate-pulse"></div>
                                                            <span>{thought}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {!message.isThinking && message.thoughts && message.thoughts.length > 0 && !message.areThoughtsCollapsed && (
                                                <div className="space-y-2 pb-2">
                                                    {message.thoughts.map((thought, i) => (
                                                        <div key={i} className="flex items-center text-sm text-slate-400">
                                                            <div className="w-1.5 h-1.5 bg-slate-500 rounded-full mr-3"></div>
                                                            <span>{thought}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xs mt-2 text-slate-500">
                                            {message.timestamp.toLocaleTimeString()}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {error && (
                                <div className="flex items-center space-x-3 bg-red-900/50 border border-red-500/30 rounded-lg p-3">
                                    <AlertTriangle size={20} className="text-red-400" />
                                    <span className="text-sm text-red-300">{error}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Message Input */}
                    <div className="p-6 border-t border-slate-700/50">
                        <div className="flex items-center space-x-2">
                            <input
                                type="text"
                                value={currentMessage}
                                onChange={(e) => setCurrentMessage(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                placeholder="Ask about elevator status, maintenance, or solutions..."
                                className="flex-1 bg-slate-700 text-white placeholder-slate-400 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                disabled={isLoading || !selectedDevice}
                            />
                            <button
                                onClick={handleSendMessage}
                                className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                                disabled={isLoading || !currentMessage.trim()}
                            >
                                <Send size={20}/>
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="p-4 text-slate-400 text-center">Please select a bank and device to view maintenance logs.</div>
            )}
        </div>

    );
}
